import type ITabletopShapeRenderer from "./shape_renderer/ITabletopShapeRenderer";

import TabletopDimensionalElement from "./TabletopDimensionalElement";
import TabletopViewport from "../TabletopViewport";
import { Edge2d, Point2d } from "../../../../common/geometry/Geom2d";
import Shape2d from "../../../../common/geometry/Shape2d";
import TabletopSimpleShapeRenderer from "./shape_renderer/TabletopSimpleShapeRenderer";
import Geom2d from "../../../../common/geometry/Geom2d";

export default class TabletopShapeElement extends TabletopDimensionalElement {
    static defaultRenderer = new TabletopSimpleShapeRenderer({
        outlineWidth: 1,
        outlineStyle: "black",
        fillColor: "grey",
    });

    private _shape: Shape2d;
    private _renderer: ITabletopShapeRenderer;

    private _highlightEdges: boolean = false;

    private _highlightedEdges: Array<number> = [];

    public constructor(args: {
        x: number | (() => number);
        y: number | (() => number);
        vertices?: Array<Point2d>;
        renderer?: ITabletopShapeRenderer;
    }) {
        super({ x: args.x, y: args.y });

        this._shape = new Shape2d(args.vertices ?? []);
        this._renderer = args.renderer ?? TabletopShapeElement.defaultRenderer;
    }

    public clone(): TabletopShapeElement {
        return new TabletopShapeElement({
            x: this.x,
            y: this.y,
            vertices: [...this._shape.vertices],
            renderer: this._renderer,
        });
    }

    public get edgeCount() {
        return this._shape.edgeCount;
    }

    public get vertices() {
        return [...this._shape.vertices];
    }
    public set vertices(val: Array<Point2d>) {
        this._shape.vertices = val;
    }

    public get edges(): Array<Edge2d> {
        return this._shape.edges;
    }

    public get edgesInUniverse(): Array<Edge2d> {
        return this._shape.edges.map((edge) => ({
            p1: { x: edge.p1.x + this.x, y: edge.p1.y + this.y },
            p2: { x: edge.p2.x + this.x, y: edge.p2.y + this.y },
        }));
    }

    /** Get the vertex at the specified index, or null if the index is out of bounds. */
    public vertex(index: number): Point2d | null {
        return this._shape.vertex(index);
    }

    /** Get the vertex at the specified index, relative to the given viewport */
    public vertexInViewport(
        index: number,
        viewport: TabletopViewport,
    ): Point2d | null {
        const vert = this.vertex(index);

        if (vert == null) {
            return null;
        }

        return {
            x: (this.x - viewport.x + vert.x) * viewport.magnificationX,
            y: (this.y - viewport.y + vert.y) * viewport.magnificationY,
        };
    }

    public get shape() {
        return this._shape;
    }

    /** Get this element's shape, translated to the given viewport */
    public shapeInViewport(viewport: TabletopViewport): Shape2d {
        return new Shape2d(
            this._shape.vertices.map((v) => ({
                x: (this.x - viewport.x + v.x) * viewport.magnificationX,
                y: (this.y - viewport.y + v.y) * viewport.magnificationY,
            })),
        );
    }

    /** Get the vertex at the specified index, relative to the given viewport */
    public vertexInUniverse(index: number): Point2d | null {
        const vert = this.vertex(index);

        if (vert == null) {
            return null;
        }

        return {
            x: this.x + vert.x,
            y: this.y + vert.y,
        };
    }

    /** Updates the position of the vertex at the specified index, without altering this shape's edge sequence. */
    public setVertexAt(index: number, vertex: Point2d): void {
        this._shape.setVertexAt(index, vertex);
    }

    /** Gets the index of the last vertex of this shape, or -1 if the shape has no vertices. */
    public get lastVertexIndex(): number {
        return this._shape.lastVertexIndex;
    }

    /** Get the last vertex of this shape. This vertex, along with the first vertex, forms the shape's last edge. */
    public get lastVertex(): Point2d | null {
        return this._shape.lastVertex;
    }

    /** Sets the position of the last vertex in this shape. This is effectively the same as calling setVertexAt for whatever index the final edge points to. */
    public setLastVertex(val: Point2d) {
        this._shape.setLastVertex(val);
    }

    /** Adds a new vertex to the end of the vertex list, without connecting it to the shape's overall structure. */
    public addVertex(vertex: Point2d): void {
        this._shape.addVertex(vertex);
    }

    public removeVertexAt(index: number): void {
        this._shape.removeVertexAt(index);
    }

    public get highlightEdges() {
        return this._highlightEdges;
    }
    public set highlightEdges(val: boolean) {
        this._highlightEdges = val;
    }

    public setHighlightedEdges(val: Array<number>) {
        this._highlightedEdges = val;
    }

    protected override onMoved(): void {
        this._shape.updateBounds();
    }

    public override get left(): number {
        return this.x + this._shape.leftBound;
    }

    public override get right(): number {
        return this.x + this._shape.rightBound;
    }

    public override get top(): number {
        return this.y + this._shape.topBound;
    }

    public override get bottom(): number {
        return this.y + this._shape.bottomBound;
    }

    public override get width(): number {
        return this.right - this.left;
    }

    public override get height(): number {
        return this.bottom - this.top;
    }

    public get renderer() {
        return this._renderer;
    }

    public set renderer(val: ITabletopShapeRenderer) {
        this._renderer = val;
    }

    public containsPointLocally(point: Point2d): boolean {
        return this._shape.containsPoint(point);
    }

    public override containsPoint(point: Point2d): boolean {
        const intersections = Geom2d.intersectionCount(
            this.left - 1,
            this.top - 1,
            point.x,
            point.y,
            ...this.edgesInUniverse,
        );

        return intersections > 0 && intersections % 2 !== 0;
    }

    public override draw(
        ctx: CanvasRenderingContext2D,
        viewport: TabletopViewport,
        timestamp: number,
    ) {
        const viewportShape = this.shapeInViewport(viewport);

        this._renderer.drawShape(ctx, viewportShape);

        if (this._highlightEdges) {
            ctx.beginPath();

            for (let idx = 0; idx < this._shape.vertices.length; ++idx) {
                const vert = this.vertexInViewport(idx, viewport) ?? {
                    x: 0,
                    y: 0,
                };

                if (idx === 0) {
                    ctx.moveTo(vert.x, vert.y);
                } else {
                    ctx.lineTo(vert.x, vert.y);
                }
            }

            ctx.closePath();

            ctx.strokeStyle = "yellow";
            ctx.lineWidth = 5;
            ctx.stroke();
        }

        if (this._highlightedEdges.length > 0) {
            ctx.beginPath();
            ctx.strokeStyle = "orange";
            ctx.lineWidth = 5;
            this._highlightedEdges.forEach((idx) => {
                const v1 = this.vertexInViewport(idx, viewport);
                const v2 = this.vertexInViewport(
                    idx === this.vertices.length - 1 ? 0 : idx + 1,
                    viewport,
                );

                if (v1 == null || v2 == null) {
                    return;
                }

                ctx.moveTo(v1.x, v1.y);
                ctx.lineTo(v2.x, v2.y);
            });
            ctx.stroke();
        }

        if (TabletopShapeElement.globalDrawElementBounds) {
            ctx.strokeStyle = "red";
            ctx.lineWidth = 1;

            ctx.strokeRect(
                (this.left - viewport.x) * viewport.magnificationX,
                (this.top - viewport.y) * viewport.magnificationY,
                this.width * viewport.magnificationX,
                this.height * viewport.magnificationY,
            );
        }
    }
}
